import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import DatePicker from "react-datepicker";
import Select from "react-select";
import moment from "moment";
import Table from "./table";
import Pagination from "./Pagination";
import DateInput from "./DateInput";
import { NavErrorBanner, NavLoading } from "./status";
import { topHoldingColumns } from "./list_of_columns";
import { groupDigits } from "./cell_formatter";
import {
  DEFAULT_PAGE_SIZE,
  NAV_PATHS,
  PAGE_SIZE_OPTIONS,
  describeError,
  useFundOptions,
  useNavRequest
} from "./api";
import messages from "./messages";
import Icon from "../../components/Icon";

/** Sum of the column widths in topHoldingColumns. */
const TABLE_MIN_WIDTH = 2130;

const EMPTY_FILTERS = {
  fundNo: undefined,
  valueDate: undefined
};

function toApiDate(date) {
  return date ? moment(date).format("YYYY-MM-DD") : undefined;
}

/**
 * funds_top_holding, every column, filtered by fund.
 *
 * There is no sort control here on purpose: `seq` is the position Portal sent and is part of the
 * read contract, so the endpoint fixes the order at fund_no ASC, seq ASC and accepts no sort key.
 */
function TopHoldingTab() {
  const intl = useIntl();
  const requestNav = useNavRequest();
  const { fundOptions, loadingFunds } = useFundOptions();

  const columns = useMemo(() => topHoldingColumns(intl), [intl]);

  const [fundOption, setFundOption] = useState(null);
  const [valueDate, setValueDate] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [pageIndex, setPageIndex] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [requesting, setRequesting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchTopHolding = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setRequesting(true);
    setErrorMessage(null);

    try {
      const result = await requestNav(NAV_PATHS.topHolding, {
        page: pageIndex + 1,
        per_page: perPage,
        fund_no: appliedFilters.fundNo,
        value_date: appliedFilters.valueDate
      });
      if (!mountedRef.current || requestIdRef.current !== requestId || result.signedOut) {
        return;
      }
      setRows(result.data);
      setTotal(Number(result.total) || 0);
    } catch (error) {
      if (!mountedRef.current || requestIdRef.current !== requestId) {
        return;
      }
      setRows([]);
      setTotal(0);
      setErrorMessage(describeError(error, intl, messages));
    } finally {
      if (mountedRef.current && requestIdRef.current === requestId) {
        setRequesting(false);
      }
    }
  }, [requestNav, intl, appliedFilters, perPage, pageIndex]);

  useEffect(() => {
    fetchTopHolding();
  }, [fetchTopHolding]);

  const applyFilters = () => {
    setPageIndex(0);
    setAppliedFilters({
      fundNo: fundOption ? fundOption.value : undefined,
      valueDate: toApiDate(valueDate)
    });
  };

  const clearFilters = () => {
    setFundOption(null);
    setValueDate(null);
    setPageIndex(0);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const perPageOptions = PAGE_SIZE_OPTIONS.map(value => ({
    value,
    label: String(value)
  }));

  const pageCount = perPage > 0 ? Math.ceil(total / perPage) : 0;

  return (
    <React.Fragment>
      {requesting ? (
        <NavLoading text={intl.formatMessage(messages.loadingTopHolding)} />
      ) : null}

      <NavErrorBanner message={errorMessage} onRetry={fetchTopHolding} />

      <div className="nav-filter-bar">
        <div className="nav-filter-field nav-filter-field-wide">
          <span className="nav-filter-label">
            {intl.formatMessage(messages.filterFund)}
          </span>
          <Select
            options={fundOptions}
            value={fundOption}
            onChange={option => setFundOption(option)}
            isClearable
            isLoading={loadingFunds}
            maxMenuHeight={220}
            className="basic-single"
            classNamePrefix="select"
            placeholder={intl.formatMessage(
              loadingFunds ? messages.filterFundLoading : messages.filterFundPlaceholder
            )}
          />
        </div>

        <div className="nav-filter-field">
          <span className="nav-filter-label">
            {intl.formatMessage(messages.filterValueDate)}
          </span>
          <div className="nav-date-range">
            <DatePicker
              selected={valueDate}
              onChange={date => setValueDate(date)}
              dateFormat="dd/MM/yy"
              customInput={<DateInput />}
            />
          </div>
        </div>

        <div className="nav-filter-field nav-filter-field-narrow">
          <span className="nav-filter-label">
            {intl.formatMessage(messages.filterRowsPerPage)}
          </span>
          <Select
            options={perPageOptions}
            value={perPageOptions.find(option => option.value === perPage)}
            onChange={option => {
              setPerPage(option.value);
              setPageIndex(0);
            }}
            maxMenuHeight={220}
            className="basic-single"
            classNamePrefix="select"
          />
        </div>

        <div className="nav-filter-actions">
          <button className="nav-search-button" onClick={() => applyFilters()}>
            <Icon name="search" size={18} />
            {intl.formatMessage(messages.buttonSearch)}
          </button>
          <button className="nav-clear-button" onClick={() => clearFilters()}>
            <Icon name="close" size={18} />
            {intl.formatMessage(messages.buttonClear)}
          </button>
        </div>
      </div>

      <p className="nav-total-rows">
        {intl.formatMessage(messages.totalRows, { total: groupDigits(total) })}
      </p>

      <Table
        columns={columns}
        data={rows}
        errorMessage={errorMessage}
        onRetry={fetchTopHolding}
        tableMinWidth={TABLE_MIN_WIDTH}
      />

      {pageCount > 1 ? (
        <Pagination nPageIndex={pageIndex} total={pageCount} gotoPage={setPageIndex} />
      ) : null}
    </React.Fragment>
  );
}

export default TopHoldingTab;
